import React, { useState, useEffect, useRef } from 'react';
import { FilterPill } from './FilterPill';
import DateField from './DateField';
import { listTaskFiles, uploadTaskFile, deleteTaskFile } from '../../services/tasks';
import '../../pages/Calendar/CalendarPage.css';
import '../../pages/Clienti/ClientiPage.css';

const STATUS_OPTS = [
  { value: 'neinceput',  label: 'Neinceput' },
  { value: 'in_progres', label: 'In progres' },
  { value: 'terminat',   label: 'Terminat' },
];

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

/* ── Multi-select dropdown ── */
export function ModalMultiSelect({ label, options, value, onChange, keyField = 'id', labelField = 'label' }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos]     = useState({ top: 0, left: 0, minWidth: 200 });
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);
  const dropRef  = useRef(null);

  const openDrop = () => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, minWidth: Math.max(r.width, 220) });
    }
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (wrapRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const toggle = (key) => {
    const next = value.includes(key) ? value.filter(v => v !== key) : [...value, key];
    onChange(next);
  };

  const filtered = query.length >= 2
    ? options.filter(o => o[labelField].toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="tm-multisel" ref={wrapRef}>
      <button type="button" className="tm-multisel-btn" onClick={openDrop}>
        <span className="tm-placeholder">{label}</span>
        <span className="tm-multisel-arrow">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div ref={dropRef} style={{ position:'fixed', top: pos.top, left: pos.left, minWidth: pos.minWidth, maxHeight: 280, display:'flex', flexDirection:'column', zIndex:9999, background:'#fff', border:'1px solid var(--border)', borderRadius: 10, boxShadow:'0 4px 20px rgba(0,0,0,.13)' }}>
          <div style={{ padding:'8px 10px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Cauta… (minim 2 caractere)"
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:6, padding:'5px 9px', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ overflowY:'auto', flex:1 }}>
            {filtered.length === 0 && <div style={{ padding:'10px 14px', fontSize:13, color:'var(--muted)' }}>{query.length < 2 ? 'Scrie pentru a cauta…' : 'Nicio optiune'}</div>}
            {filtered.map(opt => (
              <label key={opt[keyField]} style={{ display:'flex', alignItems:'center', gap: 8, padding:'7px 14px', cursor:'pointer', fontSize:13, userSelect:'none', background: value.includes(opt[keyField]) ? '#fafafa' : 'transparent' }}>
                <input type="checkbox" checked={value.includes(opt[keyField])} onChange={() => toggle(opt[keyField])} style={{ flexShrink:0, margin:0 }} />
                <span>{opt[labelField]}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="tm-pills">
          {options.filter(o => value.includes(o[keyField])).map(o => (
            <span key={o[keyField]} className="tm-pill">
              {o[labelField]}
              <button type="button" onClick={() => toggle(o[keyField])}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Task Modal ── */
export const EMPTY_TASK_FORM = {
  nume: '', status: 'neinceput', asignat_la: [], subtaskuri: [],
  data: '', toata_ziua: true, termen_limita: '', trimite_reminder_email: false,
  clienti_ids: [], proceduri_ids: [], descriere: '',
};

export default function TaskModal({
  task,
  prefill,        // { clienti_ids, proceduri_ids } — used when creating from a procedure context
  staffUsers,
  clienti,
  allProceduri,
  onSave,
  onDelete,
  onClose,
  saving,
  deleting,
}) {
  const [form, setForm] = useState(() => {
    if (task) {
      return {
        nume:          task.nume || '',
        status:        task.status || 'neinceput',
        asignat_la:    task.asignat_la || [],
        subtaskuri:    task.subtaskuri || [],
        data:          task.data || '',
        toata_ziua:    task.toata_ziua !== false,
        termen_limita: task.termen_limita || '',
        trimite_reminder_email: !!task.trimite_reminder_email,
        clienti_ids:   task.clienti_ids || [],
        proceduri_ids: task.proceduri_ids || [],
        descriere:     task.descriere || '',
      };
    }
    return {
      ...EMPTY_TASK_FORM,
      ...(prefill || {}),
    };
  });

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  /* ── Documents ── */
  const fileInputRef = useRef(null);
  // For edit mode: files already saved on the server
  const [existingFiles,  setExistingFiles]  = useState([]);
  const [filesLoading,   setFilesLoading]   = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  // For both modes: pending files to upload (will be uploaded after task save for new tasks,
  // or immediately for edit tasks)
  const [pendingFiles,   setPendingFiles]   = useState([]); // [{desc, file, uploading}]
  const [uploadingIdx,   setUploadingIdx]   = useState(null);

  // Load existing files when editing
  useEffect(() => {
    if (!task?.id) return;
    setFilesLoading(true);
    listTaskFiles(task.id)
      .then(d => setExistingFiles(d.files || []))
      .catch(() => {})
      .finally(() => setFilesLoading(false));
  }, [task?.id]);

  const addPendingFile = (file) => {
    setPendingFiles(ps => [...ps, { desc: '', file, uploading: false }]);
  };

  const removePending = (idx) => setPendingFiles(ps => ps.filter((_, i) => i !== idx));
  const setPendingDesc = (idx, desc) => setPendingFiles(ps => ps.map((p, i) => i === idx ? { ...p, desc } : p));

  // Upload a single pending file to an existing task
  const uploadPendingToTask = async (taskId, idx) => {
    const p = pendingFiles[idx];
    const fd = new FormData();
    fd.append('file', p.file);
    fd.append('denumire', p.desc);
    setUploadingIdx(idx);
    try {
      const res = await uploadTaskFile(taskId, fd);
      setExistingFiles(prev => [res.file, ...prev]);
      removePending(idx);
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleDeleteExisting = async (fileId) => {
    if (!window.confirm('Stergi acest fisier?')) return;
    setDeletingFileId(fileId);
    try {
      await deleteTaskFile(fileId);
      setExistingFiles(prev => prev.filter(f => f.id !== fileId));
    } finally {
      setDeletingFileId(null);
    }
  };

  const addSubtask = () => set({ subtaskuri: [...form.subtaskuri, { text: '', done: false }] });
  const setSubtask = (i, val) => set({ subtaskuri: form.subtaskuri.map((s, j) => j === i ? { ...s, text: val } : s) });
  const delSubtask = (i) => set({ subtaskuri: form.subtaskuri.filter((_, j) => j !== i) });

  const staffOpts   = staffUsers.map(u => ({ id: u.id, label: u.full_name }));
  const clientiOpts = clienti.map(c => ({ id: c.id, label: c.nume }));

  const filteredProceduri = form.clienti_ids.length > 0
    ? allProceduri.filter(p => form.clienti_ids.includes(p.client_id))
    : allProceduri;
  const procOpts = filteredProceduri.map(p => ({
    id: p.id,
    label: `${p.data?.denumire || p.data?.numar || p.tip_proc} — ${p.client_name || ''}`,
  }));

  const handleClientChange = (ids) => {
    const validProcIds = ids.length > 0
      ? allProceduri.filter(p => ids.includes(p.client_id)).map(p => p.id)
      : allProceduri.map(p => p.id);
    set({ clienti_ids: ids, proceduri_ids: form.proceduri_ids.filter(id => validProcIds.includes(id)) });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box tm-modal-box" onClick={e => e.stopPropagation()}>

        <div className="modal-sticky-header">
          <div className="op-modal-head">
            <div className="op-modal-title">{task ? 'Editeaza task' : 'Task nou'}</div>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-scroll-body">
          <div className="tm-form">

            <div className="tm-field">
              <label className="tm-label">Nume <span style={{ color:'#ef4444' }}>*</span></label>
              <input className="tm-input" type="text" value={form.nume} onChange={e => set({ nume: e.target.value })} placeholder="Titlul taskului…" autoFocus />
            </div>

            <div className="tm-field">
              <label className="tm-label">Asignat la</label>
              <ModalMultiSelect label="Selectati utilizatori…" options={staffOpts} value={form.asignat_la} onChange={v => set({ asignat_la: v })} />
            </div>

            <div className="tm-field">
              <label className="tm-label">Status</label>
              <FilterPill value={form.status} onChange={e => set({ status: e.target.value })}>
                <option value="">— Status —</option>
                {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </FilterPill>
            </div>

            <div className="tm-field">
              <label className="tm-label">Subtaskuri</label>
              <div className="tm-subtasks">
                {form.subtaskuri.map((s, i) => (
                  <div key={i} className="tm-subtask-row">
                    <input className="tm-input" type="text" value={s.text} onChange={e => setSubtask(i, e.target.value)} placeholder={`Subtask ${i + 1}…`} />
                    <button type="button" className="tm-del-btn" onClick={() => delSubtask(i)}>×</button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf:'flex-start', marginTop: 4 }} onClick={addSubtask}>+ Adauga subtask</button>
              </div>
            </div>

            <div className="tm-field">
              <label className="tm-label">Data</label>
              <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
                <div style={{ width: 180 }}>
                  <DateField value={form.data} onChange={v => set({ data: v })} />
                </div>
                <label style={{ display:'flex', alignItems:'center', gap: 6, fontSize: 13, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.toata_ziua} onChange={e => set({ toata_ziua: e.target.checked })} />
                  Toata ziua
                </label>
              </div>
            </div>

            <div className="tm-field">
              <label className="tm-label">Termen limita</label>
              <div style={{ width: 180 }}>
                <DateField value={form.termen_limita} onChange={v => set(v ? { termen_limita: v } : { termen_limita: v, trimite_reminder_email: false })} />
              </div>
              <span className="tm-hint">Completati doar daca acest task are o data limita de realizare</span>
              {form.termen_limita && (
                <label style={{ display:'flex', alignItems:'center', gap: 6, fontSize: 13, cursor:'pointer', marginTop: 8 }}>
                  <input type="checkbox" checked={!!form.trimite_reminder_email} onChange={e => set({ trimite_reminder_email: e.target.checked })} />
                  Trimite reminder pe email
                </label>
              )}
            </div>

            <div className="tm-field">
              <label className="tm-label">Client</label>
              <ModalMultiSelect label="Selectati clienti…" options={clientiOpts} value={form.clienti_ids} onChange={handleClientChange} />
            </div>

            <div className="tm-field">
              <label className="tm-label">Procedura</label>
              <ModalMultiSelect label="Selectati proceduri…" options={procOpts} value={form.proceduri_ids} onChange={v => set({ proceduri_ids: v })} />
            </div>

            <div className="tm-field">
              <label className="tm-label">Scurta descriere</label>
              <textarea className="tm-textarea" rows={4} value={form.descriere} onChange={e => set({ descriere: e.target.value })} placeholder="Detalii, context, instructiuni…" />
            </div>

            {task?.created_by_name && (
              <div className="tm-created">
                Creat de <strong>{task.created_by_name}</strong>
                {task.created_at ? ` — ${fmtDateTime(task.created_at)}` : ''}
              </div>
            )}

            {/* ── Documents section ── */}
            <div className="tm-field tm-docs-section">
              <label className="tm-label">Documente</label>

              {/* Existing files (edit mode) */}
              {filesLoading && <div className="doc-empty">Se incarca…</div>}
              {existingFiles.map(f => (
                <div key={f.id} className="doc-saved-row">
                  <div className="doc-saved-info">
                    {f.denumire && <span className="doc-saved-type">{f.denumire}</span>}
                    <a className="doc-saved-name" href={f.url} target="_blank" rel="noreferrer">{f.filename}</a>
                    {f.uploaded_by && <span style={{ fontSize: 11.5, color: 'var(--muted)', flexShrink: 0 }}>de {f.uploaded_by}</span>}
                  </div>
                  <div className="doc-saved-actions">
                    <button
                      type="button" className="btn btn-ghost btn-xs"
                      style={{ color: '#ef4444' }}
                      disabled={deletingFileId === f.id}
                      onClick={() => handleDeleteExisting(f.id)}
                    >{deletingFileId === f.id ? '…' : 'Sterge'}</button>
                  </div>
                </div>
              ))}

              {/* Pending files (to be uploaded) */}
              {pendingFiles.map((p, idx) => (
                <div key={idx} className="doc-pending-row">
                  <input
                    className="doc-custom-input"
                    type="text"
                    placeholder="Nume / descriere document"
                    value={p.desc}
                    onChange={e => setPendingDesc(idx, e.target.value)}
                  />
                  <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{p.file.name}</span>
                  {task?.id && (
                    <button
                      type="button" className="btn btn-secondary btn-xs"
                      disabled={uploadingIdx === idx}
                      onClick={() => uploadPendingToTask(task.id, idx)}
                    >{uploadingIdx === idx ? '…' : 'Upload'}</button>
                  )}
                  <button type="button" className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => removePending(idx)}>✕</button>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-secondary btn-sm tm-add-doc-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                + Adauga document
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) { addPendingFile(e.target.files[0]); e.target.value = ''; } }}
              />
            </div>
          </div>
        </div>

        <div className="tm-footer">
          <div style={{ display:'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose} disabled={saving || deleting}>Anuleaza</button>
            <button className="btn btn-primary btn-sm" type="button" onClick={() => onSave(form, pendingFiles)} disabled={saving || deleting || !form.nume.trim()}>
              {saving ? 'Se salveaza…' : 'Salveaza'}
            </button>
          </div>
          {task && (
            <button className="btn btn-sm tm-delete-btn" type="button" onClick={onDelete} disabled={saving || deleting}>
              {deleting ? 'Se sterge…' : 'Sterge'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
